import LookupService from "./lookupService";
import {DateTime} from "luxon";
import {appCache} from "../../cache/slices/app/appSlice";
import {
    APPOINTMENT_START_STOP_DATE_TIME_FORMAT,
    dateToString,
    dateToStringWithFormat,
    formatClientNameWithoutId
} from "../utils/formatUtils";
import api from "./api";
import {getClientProgramFromAppointment, isAddressRequiredForOrganization} from "../utils/appointmentUtils";
import {combineDateAndTime} from "../utils/dateTimeUtils";
import {getDispatchFromConfig} from "../utils/miscUtils";
import syncService from "./syncService";
import { findVisitAndUpdate, findVisitAndRemove } from "../utils/visitUtils";
import { evvRepository } from "../../db/evv";
import {
    generateSyncId,
    canProcess,
    shouldRetry,
    SYNC_STATUS
} from "../utils/syncUtils";
import { SYNC_STATE_DIRTY, SYNC_STATE_CLEAN } from "../constants";
import { emitDeadlockIfDetected, ERROR_SOURCES } from "../utils/syncError";

class AppointmentService extends LookupService{
    getFetchParameters = (parameters) => {
        return parameters ? {"patientId": parameters.clientId} : {};
    };

    saveAppointment = (appointment, config) => {
        console.log('AppointmentService.saveAppointmentAndVisit - config:');
        console.log(config);
        if (appointment) {
            const ignoreVisitSave = (config.ignoreVisitSave === true);
            if (!appointment.appointmentId) {
                appointment.appointmentId = (-1 * Date.now());
                appointment.evvVisitId = appointment.appointmentId;
            }

            if (appointment.staffId)
                appointment.syncId = appointment.syncId ? appointment.syncId : generateSyncId(appointment.staffId);

            appointment.syncState = SYNC_STATE_DIRTY;

            const appointments = [appointment];

            return this.save(appointments, config)
                .then(() => {
                    return this.writeAppointmentsToServer(this.afterLoad(appointments, config), config)
                        .then(savedAppointments => {
                            let savedAppointment = null;

                            if (savedAppointments && savedAppointments.length > 0){
                                const dispatch = getDispatchFromConfig(config);
                                savedAppointment = savedAppointments[0];

                                dispatch(this.operationReplace(savedAppointment));

                                // Call saveVisitForAppointment only when appointment synced successfully (statusSync='Success').
                                // This prevents creating visits for appointments that failed with 5xx errors or deadlocks,
                                if (canProcess(savedAppointment.statusSync) && !ignoreVisitSave) {
                                    return syncService.visitService.saveVisitForAppointment(savedAppointment, config)
                                        .then(() => savedAppointment);
                                }
                            }

                            return savedAppointment;
                        })
                })
        }
    }

    removeAppointment = (appointment, config) => {
        if (appointment) {
            this.deleteByPrimaryKey(appointment.appointmentId)
                .then(() => {
                    return this.load(config);
                })
        }
    }

    beforeSave = (appointments, config) => {
        return appointments.map(appointment => {
            if (!appointment.syncState){
                appointment.syncState = "clean";
            }

            return appointment;
        });
    }

    afterLoad = (appointments, config) => {
        if (appointments){
            const state = config.store.getState();
            const clients = appCache.getClients(state);
            const activities = syncService.getActivities(state);
            const organizations = syncService.getOrganizations(state);
            const programs = syncService.getPrograms(state);
            const serviceLocations = syncService.getServiceLocations(state);

            console.log("appointments.afterLoad - initial");
            console.log(appointments);

            appointments.forEach(appointment => {
                const appointmentDateInstance = (appointment.appointmentDate ? DateTime.fromISO(appointment.appointmentDate.substring(0,10)) : DateTime.now()).startOf('day');
                const startDateInstance = combineDateAndTime(appointmentDateInstance, appointment.startTime);
                let endDateInstance = combineDateAndTime(appointmentDateInstance, appointment.endTime);
                if (endDateInstance < startDateInstance){
                    endDateInstance = combineDateAndTime(startDateInstance.plus({days: 1}), appointment.endTime);
                    console.log("Overnight ---> Id : "+appointment.appointmentId + "Apptointment Date: " + dateToString(appointmentDateInstance) + " *** Start : " + dateToString(startDateInstance) + " *** End : " + dateToString(endDateInstance));
                }
                appointment.startDateInstance = startDateInstance;
                appointment.endDateInstance = endDateInstance;
                appointment.start = startDateInstance.toJSDate();
                appointment.end = endDateInstance.toJSDate();

                this.fillAppointmentFields(
                    appointment, clients, activities, organizations, programs, serviceLocations);

                appointment.title = appointment.client ? formatClientNameWithoutId(appointment.client) : `Unknown Client (${appointment.clientId})`;
            });
        }
        
        return appointments;
    }

    fillAppointmentFields = (appointment, clients, activities, organizations, programs, serviceLocations) => {
        if (!appointment.client) {
            // eslint-disable-next-line eqeqeq
            appointment.client = clients && clients.find(c => c.clientId == appointment.clientId);
        }
        if (!appointment.activity) {
            appointment.activity = activities && activities.find(a => a.activityId === appointment.activityId);
        }
        if (!appointment.organization) {
            appointment.organization = organizations && organizations.find(o => o.organizationId === appointment.organizationId);
        }
        if (!appointment.program) {
            appointment.program = programs && programs.find(p => p.programId === appointment.programId);
        }
        if (!appointment.serviceLocation) {
            appointment.serviceLocation = serviceLocations && serviceLocations.find(sl => sl.serviceLocationId === appointment.serviceLocationId);
        }
    }

    createWriteAppointment = (appointment, clientPrograms) => {
        const beginTime = dateToStringWithFormat(appointment.startDateInstance, APPOINTMENT_START_STOP_DATE_TIME_FORMAT );
        const endTime = dateToStringWithFormat(appointment.endDateInstance, APPOINTMENT_START_STOP_DATE_TIME_FORMAT );
        const clientProgram = getClientProgramFromAppointment(appointment, clientPrograms);
        const excludeActualTimes = appointment.appointmentId < 0 && appointment.status === 'None';

        if (!clientProgram){
            const message = `Unable to determine client program: Client[${appointment.clientId}] Program[${appointment.programId}] Org[${appointment.organizationId}].`;
            console.log(message);
            console.log(clientPrograms);
            this.deleteByPrimaryKey(appointment.appointmentId);
            throw new Error(message);
        }

        const writeAppointment = {
            staffId: appointment.staffId,
            clientId: appointment.clientId,
            clientProgramId: clientProgram?.clientProgramId,
            organizationId: appointment.organizationId,
            appointmentDate: appointment.appointmentDate,
            beginTime: beginTime,
            endTime: endTime,
            activityId: appointment.activityId,
            serviceLocationId: appointment.serviceLocationId,
            serviceDocumentId: '',
            nonBillableMinutes: appointment.nonBillableMinutes,
            status: appointment.status,
            syncId: appointment.syncId
        };

        if (!excludeActualTimes){
            writeAppointment.actualStartTime = beginTime;
            writeAppointment.actualEndTime = endTime;
        }

        if (appointment.appointmentId > 0){
            writeAppointment.activityDetailId = appointment.activityDetailId;
            writeAppointment.activityLogId = appointment.appointmentId;
        }

        if (appointment?.status === 'Kept' && appointment?.activityDetailDescriptorList){
            writeAppointment.activityQualifiers = appointment?.activityDetailDescriptorList.filter(add => Number.isInteger(add?.descriptorId) && add?.descriptorId > 0);
        } else {
            writeAppointment.activityQualifiers = [];
        }

        if (isAddressRequiredForOrganization(appointment.organization)){
            if (appointment.startVisitAddress){
                writeAppointment.sameAsEndAddress = appointment.sameAsEndAddress;
                writeAppointment.startVisitAddress = {...appointment.startVisitAddress};
            }
            if (appointment.endVisitAddress){
                writeAppointment.sameAsEndAddress = appointment.sameAsEndAddress;
                writeAppointment.endVisitAddress = {...appointment.endVisitAddress};
            }
        }

        return writeAppointment;
    }

    saveAppointments = async(config, appointmentsToSave) => {
        console.log('AppointmentService.writeAppointmentsToServer - config:');
        console.log(config);
        return this.save(appointmentsToSave, {store: config.store, doNotClear: true})
            .then(() => {
                console.log("appointmentService.writeAppointmentsToServer: records saved successfully");
                console.log(appointmentsToSave);
                return Promise.resolve(appointmentsToSave);
            });
    }

loopServerAppointments = async (appointments, writeAppointmentResponse, config) => {
    const appointmentsToSave = [];
    const appointmentsError = [];
    let hasDeadlock = false;
    const dispatch = getDispatchFromConfig(config);

    const resources = writeAppointmentResponse.resources || [];
    
    for (let index = 0; index < resources.length; index++) {
        try {
            const appointmentFromServer = resources[index];
            const originalAppointment = appointments[index];
            
            const result = await this.processAppointmentFromServer(
                appointmentFromServer,
                originalAppointment,
                config,
                dispatch
            );

            if (result.hasError) {
                appointmentsError.push(...result.errors);
            } else {
                if (result.hasDeadlock) {
                    hasDeadlock = true;
                }
                appointmentsToSave.push(result.appointmentToSave);
            }
        } catch (error) {
            console.error(`[loopServerAppointments] Error processing appointment at index ${index}:`, error);
            throw error;
        }
    }

    return this.finalizeAppointmentProcessing(
        appointments,
        appointmentsToSave,
        appointmentsError,
        hasDeadlock,
        config
    );
}

processAppointmentFromServer = async (appointmentFromServer, originalAppointment, config, dispatch) => {
        const appointmentToSave = {...originalAppointment};
    
    if (appointmentFromServer.statusSync) {
        appointmentToSave.statusSync = appointmentFromServer.statusSync;
    }

    // Handle business errors from backend
    if (this.hasBusinessErrors(appointmentFromServer)) {
        return this.handleBusinessErrors(appointmentFromServer, originalAppointment, config);
    }

    // Process successful or retryable appointment
    this.updateSyncState(appointmentToSave, appointmentFromServer);
    const hasDeadlock = this.checkForDeadlock(appointmentFromServer);

    await this.handleAppointmentScenarios(
        appointmentFromServer,
        originalAppointment,
        appointmentToSave,
        config
    );

    const finalAppointment = this.updateAddresses(
        appointmentToSave,
        appointmentFromServer,
        originalAppointment
    );
    
    this.removeOldAppointmentIfNeeded(originalAppointment, dispatch);

    return {
        hasError: false,
        hasDeadlock,
        appointmentToSave: finalAppointment
    };
}

    hasBusinessErrors = (appointmentFromServer) => {
        return appointmentFromServer.error && appointmentFromServer.error.length > 0;
    }

    handleBusinessErrors = (appointmentFromServer, originalAppointment, config) => {
        this.deleteByPrimaryKey(originalAppointment.appointmentId);
        findVisitAndRemove(config, originalAppointment);
        
        return {
            hasError: true,
            errors: appointmentFromServer.error,
            hasDeadlock: false,
            appointmentToSave: null
        };
    }

    updateSyncState = (appointmentToSave, appointmentFromServer) => {
        if (shouldRetry(appointmentFromServer.statusSync)) {
            appointmentToSave.syncState = SYNC_STATE_DIRTY;
        } else {
            appointmentToSave.syncState = SYNC_STATE_CLEAN;
        }
    }

    checkForDeadlock = (appointmentFromServer) => {
        return appointmentFromServer.statusSync === SYNC_STATUS.DEADLOCK_EXCEPTION;
    }

    handleAppointmentScenarios = async (appointmentFromServer, originalAppointment, appointmentToSave, config) => {
        const isNewAppointment = originalAppointment.appointmentId < 0 && appointmentFromServer.activityLogId;
        const isDeduplicatedAppointment = appointmentFromServer.visitId > 0 && appointmentToSave.evvVisitId < 0;
        const isErrorWithoutLog = shouldRetry(appointmentFromServer.statusSync);
        const isRecoveredFromError = canProcess(appointmentFromServer.statusSync) && shouldRetry(originalAppointment.statusSync);

        if (isNewAppointment) {
            await this.handleNewAppointment(appointmentFromServer, originalAppointment, appointmentToSave, config);
        } else if (isDeduplicatedAppointment) {
            await this.handleDeduplicatedAppointment(appointmentFromServer, originalAppointment, appointmentToSave, config);
        } else if (isErrorWithoutLog) {
            await this.handleErrorWithoutActivityLog(originalAppointment, config);
        } else if (isRecoveredFromError) {
            await this.updateVisitForSuccessfulRetry(config, appointmentToSave);
        }
    }

    handleNewAppointment = async (appointmentFromServer, originalAppointment, appointmentToSave, config) => {
        // Update appointment with server IDs
        appointmentToSave.appointmentId = appointmentFromServer.activityLogId;
        appointmentToSave.activityDetailId = appointmentFromServer.activityDetailId;
        appointmentToSave.evvVisitId = appointmentFromServer.visitId;

        // Update visit based on status
        await this.updateVisitForNewAppointment(appointmentFromServer, originalAppointment, appointmentToSave, config);

        // Update related documents
        syncService.documentService.updateAppointmentDocument(
            originalAppointment.appointmentId,
            appointmentToSave.activityDetailId,
            appointmentToSave.appointmentId
        );

        // Log and cleanup old temporary appointment
        console.log(`Original temp appointment id: ${originalAppointment.appointmentId} - final appointment id from server: ${appointmentToSave.appointmentId}`);
        console.log(`DELETING original temp appointment with id: [${originalAppointment.appointmentId}] to avoid duplicates.`);
        this.deleteByPrimaryKey(originalAppointment.appointmentId);
    }

    updateVisitForNewAppointment = async (appointmentFromServer, originalAppointment, appointmentToSave, config) => {
        const canProcessAppointment = canProcess(appointmentFromServer.statusSync) || !appointmentFromServer.statusSync;
        const shouldRetryAppointment = shouldRetry(appointmentFromServer.statusSync);

        if (canProcessAppointment) {
            findVisitAndUpdate(config, appointmentFromServer, originalAppointment, appointmentToSave);
        } else if (shouldRetryAppointment) {
            await this.createVisitForDeadLockAppointment(config, appointmentFromServer, appointmentToSave);
        }
    }

    handleDeduplicatedAppointment = async (appointmentFromServer, originalAppointment, appointmentToSave, config) => {
        // Update visit ID from server response
        appointmentToSave.evvVisitId = appointmentFromServer.visitId;

        // Update documents if this was a deduplicated appointment
        if (originalAppointment._originalNegativeId) {
            syncService.documentService.updateAppointmentDocument(
                originalAppointment._originalNegativeId,
                appointmentToSave.activityDetailId || appointmentFromServer.activityDetailId,
                appointmentToSave.appointmentId
            );

            // Cleanup old negative-ID record
            this.deleteByPrimaryKey(originalAppointment._originalNegativeId);
            const dispatch = getDispatchFromConfig(config);
            if (dispatch) {
                dispatch(this.operationRemove({ appointmentId: originalAppointment._originalNegativeId }));
            }
        }
    }

    handleErrorWithoutActivityLog = async (originalAppointment, config) => {
        const linkedVisit = await evvRepository.evvDb.visit
            .filter(v => v.syncId === originalAppointment.syncId)
            .first();

        if (linkedVisit) {
            await this.markVisitAsError(linkedVisit);
        } else if (originalAppointment.appointmentId < 0) {
            await this.createErrorVisit(originalAppointment, config);
        }
    }

    markVisitAsError = async (visit) => {
        if (!shouldRetry(visit.statusSync)) {
            visit.statusSync = SYNC_STATUS.ERROR;
            visit.syncState = SYNC_STATE_DIRTY;
            await evvRepository.evvDb.visit.put(visit);
        }
    }

    createErrorVisit = async (originalAppointment, config) => {
        const newVisit = syncService.visitService.updateVisitFromAppointment(null, originalAppointment);
        newVisit.statusSync = SYNC_STATUS.ERROR;
        newVisit.syncState = SYNC_STATE_DIRTY;
        newVisit.isRecordOnline = !syncService.visitService.isInformationOffline(config);
        await evvRepository.evvDb.visit.put(newVisit);
    }

    updateAddresses = (appointmentToSave, appointmentFromServer, originalAppointment) => {
        if (!isAddressRequiredForOrganization(originalAppointment.organization)) {
            return appointmentToSave;
        }

        const finalAppointmentToSave = {
            ...appointmentToSave
        };

        finalAppointmentToSave.sameAsEndAddress = appointmentFromServer.sameAsEndAddress;

        if (appointmentFromServer.startVisitAddress) {
            finalAppointmentToSave.startVisitAddress = appointmentFromServer.startVisitAddress;
        }

        if (appointmentFromServer.endVisitAddress) {
            finalAppointmentToSave.endVisitAddress = appointmentFromServer.endVisitAddress;
        }

        return finalAppointmentToSave;
    };

    removeOldAppointmentIfNeeded = (originalAppointment, dispatch) => {
        if (originalAppointment.appointmentId < 0) {
            dispatch(this.operationRemove(originalAppointment));
        }
    }

    finalizeAppointmentProcessing = async (appointments, appointmentsToSave, appointmentsError, hasDeadlock, config) => {
        emitDeadlockIfDetected(hasDeadlock, ERROR_SOURCES.APPOINTMENT);

        // Save successful appointments
        let savedAppointments = appointments;
        if (appointmentsToSave.length > 0) {
            savedAppointments = await this.saveAppointments(config, appointmentsToSave);
        }

        // Execute Write Visit before throwing the error
        // This ensures that visits are written for successful appointments
        // even when there are appointments with business errors
        if (savedAppointments?.length > 0) {
            const successfulAppointments = savedAppointments.filter(a =>
                canProcess(a.statusSync) || !a.statusSync
            );

            if (successfulAppointments.length > 0) {
                try {
                    await syncService.visitService.dependentWriteToServer(config, successfulAppointments);
                } catch (visitError) {
                    console.error('Error in dependentWriteToServer:', visitError);
                    // Continue to throw the appointments error
                }
            }
        }

        // Now throw error if there are appointments with business errors
        // The error will propagate to the AppointmentDialog to display the modal
        if (appointmentsError.length > 0) {
            throw new Error(this.errorFromServer(appointmentsError));
        }

        return Promise.resolve(savedAppointments);
    }

    createVisitForDeadLockAppointment = async (config, appointmentFromServer, appointmentToSave) => {
        try {
            let existingVisit = null;
            if (appointmentToSave.syncId) {
                existingVisit = await evvRepository.evvDb.visit
                    .where('syncId')
                    .equals(appointmentToSave.syncId)
                    .first();
            }

            if (!existingVisit) {
                return;
            }

            // Overwrite specific fields for DeadLock
            existingVisit.evvVisitId = appointmentFromServer.visitId;
            existingVisit.syncState = SYNC_STATE_DIRTY;
            existingVisit.statusSync = SYNC_STATUS.DEADLOCK_EXCEPTION;
            existingVisit.appointmentId = appointmentToSave.appointmentId;
            
            if (existingVisit.actualStartDateInstance) {
                delete existingVisit.actualStartDateInstance;
            }
            if (existingVisit.actualEndDateInstance) {
                delete existingVisit.actualEndDateInstance;
            }

            await evvRepository.evvDb.visit.put(existingVisit);

            try {
                const dispatch = getDispatchFromConfig(config);
                if (dispatch && syncService?.visitService?.operationAdd) {
                    dispatch(syncService.visitService.operationAdd(existingVisit));
                }
            } catch (reduxError) {
                console.warn('Error updating Redux state for DeadLock visit:', reduxError);
            }

        } catch (error) {
            console.error('Error creating visit for DeadLock appointment:', error);
            console.error('Error stack:', error.stack);
        }
    }

    updateVisitForSuccessfulRetry = async (config, appointmentToSave) => {
        try {
            const relatedVisit = await evvRepository.evvDb.visit
                .where('syncId')
                .equals(appointmentToSave.syncId)
                .first();

            if (relatedVisit) {
                // If the visit has Error status, it was never synced to the server (e.g. created during a 504).
                // Don't mark it as Success - leave it dirty so dependentWriteToServer picks it up this cycle.
                const newStatusSync = relatedVisit.statusSync === SYNC_STATUS.ERROR
                    ? relatedVisit.statusSync
                    : SYNC_STATUS.SUCCESS;

                const updatedVisit = {
                    ...relatedVisit,
                    statusSync: newStatusSync,
                    appointment: appointmentToSave,
                    syncState: SYNC_STATE_DIRTY
                };

                await evvRepository.evvDb.visit.put(updatedVisit);
                const processedVisit = syncService.visitService.afterLoad([updatedVisit], config)[0];

                // Update Redux with the visit processed
                const dispatch = getDispatchFromConfig(config);
                dispatch(syncService.visitService.operationReplace(processedVisit));
            }
        } catch (error) {
            console.error('Error updating visit for successful appointment retry:', error);
            console.error('Error stack:', error.stack);
        }
    }

    errorFromServer = (appointmentsError) => {
        let error = '';
        if (appointmentsError?.length === 1) {
            error = appointmentsError[0];
        } else if (appointmentsError?.length > 1) {
            for (let i = 0; i < appointmentsError.length; i++) {
                error += appointmentsError[i] + "\n";
            }
        }
        return error;
    }

    writeAppointmentsToServer = async (appointments, config) => {
        let clientPrograms = syncService.getClientPrograms(config.store.getState());
        if (clientPrograms.length === 0){
            clientPrograms = await syncService.clientProgramService.loadFromRepository(config);
        }

        const appointmentsToPost = [];
        const alreadySyncedAppointmentsResponse = { resources: [] };

        // Preload appointments from server grouped by client
        const appointmentsCache = await this.preloadAppointmentsByClients(appointments, config);

        for (const appointment of appointments) {
            if (appointment.appointmentId < 0) {
                const syncedMatch = await this.findAppointmentAlreadySynced(appointment, config, appointmentsCache);
                if (syncedMatch?.appointmentId) {
                    // If the server has a log and locally we have statusSync === 'Error' (previous 504)
                    // We bypass the deduplication skip by injecting the server ID to force an update
                    if (appointment.statusSync === SYNC_STATUS.ERROR) {
                        appointment._originalNegativeId = appointment.appointmentId; // save before mutation to clean up later
                        appointment.appointmentId = syncedMatch.appointmentId;
                        appointment.activityDetailId = syncedMatch.activityDetailId;
                        appointmentsToPost.push(appointment);
                        continue;
                    }

                    alreadySyncedAppointmentsResponse.resources.push({
                        activityLogId: syncedMatch.appointmentId,
                        activityDetailId: syncedMatch.activityDetailId,
                        visitId: syncedMatch.evvVisitId,
                        originalDirtyId: appointment.appointmentId
                    });
                    continue;
                }
            }
            appointmentsToPost.push(appointment);
        }

        let loopResponsePromise = Promise.resolve(appointments);

        if (appointmentsToPost.length > 0) {
            const writeAppointments = appointmentsToPost.map(appointment => this.createWriteAppointment(appointment, clientPrograms));

            const writeAppointmentRequest = {
                resources: writeAppointments
            };

            console.log("Writing appointment to server: ");
            console.log(writeAppointmentRequest);

            this.updateAppointmentDocumentDb(appointmentsToPost);

            loopResponsePromise = api.post(this.writeUrl, this.getFetchConfig(config, writeAppointmentRequest))
                .then(async writeAppointmentResponse => {
                    console.log("Succes writing appointment to server: ");
                    console.log(writeAppointmentResponse);

                    // Merge already checked responses 
                    if (alreadySyncedAppointmentsResponse.resources.length > 0) {
                        if (!writeAppointmentResponse.resources) writeAppointmentResponse.resources = [];

                        alreadySyncedAppointmentsResponse.resources.forEach(preChecked => {
                            // Find the correct index to insert into the loop Server array mapping
                            const originalIndex = appointments.findIndex(a => a.appointmentId === preChecked.originalDirtyId);
                            if (originalIndex >= 0) {
                                writeAppointmentResponse.resources.splice(originalIndex, 0, preChecked);
                            }
                        });
                    }

                    return this.loopServerAppointments(appointments, writeAppointmentResponse, config);
                })
                .catch(async error => {
                    const errorMessage = error.message || error.toString();

                    if (errorMessage.startsWith('ERROR')) {
                        return Promise.reject(error);
                    }

                    if (api.isConnectionLost(error)) {
                        const appointmentsToMarkError = appointmentsToPost;
                        let modifiedAppts = [];
                        if (appointmentsToMarkError.length > 0) {
                            modifiedAppts = appointmentsToMarkError.map(a => ({ ...a, statusSync: 'Error', syncState: 'dirty' }));
                            await this.save(modifiedAppts, { ...config, doNotClear: true });

                            for (const appt of modifiedAppts) {
                                const linkedVisit = await evvRepository.evvDb.visit
                                    .filter(v => v.syncId === appt.syncId)
                                    .first();

                                if (linkedVisit) {
                                    const updateVisit = syncService.visitService.updateVisitFromAppointment(linkedVisit, appt);
                                    updateVisit.statusSync = SYNC_STATUS.ERROR;
                                    updateVisit.syncState = SYNC_STATE_DIRTY;
                                    updateVisit.isRecordOnline = !syncService.visitService.isInformationOffline(config);
                                    await evvRepository.evvDb.visit.put(updateVisit);
                                } else {
                                    const newVisit = syncService.visitService.updateVisitFromAppointment(null, appt);
                                    newVisit.statusSync = SYNC_STATUS.ERROR;
                                    newVisit.syncState = SYNC_STATE_DIRTY;
                                    newVisit.isRecordOnline = !syncService.visitService.isInformationOffline(config);
                                    newVisit.isPendSendStartSessInfo = false;
                                    await evvRepository.evvDb.visit.put(newVisit);
                                }
                            }
                        }
                        return modifiedAppts.length > 0 ? modifiedAppts : appointments;
                    } else {
                        return Promise.reject(error);
                    }
                });
        } else if (alreadySyncedAppointmentsResponse.resources.length > 0) {
            // All appointments were deduplicated, loop directly
            loopResponsePromise = this.loopServerAppointments(appointments, alreadySyncedAppointmentsResponse, config);
        }

        return loopResponsePromise;
    }

    /**
     * Preloads appointments from the server grouped by client to optimize deduplication.
     * 
     * Instead of calling read_appointment API once per appointment (N calls),
     * this method calls it once per unique client (M calls where M << N).
     * 
     * Example: 4 appointments for AKClient + 1 for PAClient = 2 API calls instead of 5
     * 
     * @param {Array} appointments - List of dirty appointments to sync
     * @param {Object} config - Sync configuration
     * @returns {Map<number, Array>} Map of clientId -> appointments from server
     */
    preloadAppointmentsByClients = async (appointments, config) => {
        const appointmentsCache = new Map();

        // Only preload for negative appointments (new/offline appointments)
        const negativeAppointments = appointments.filter(a => a.appointmentId < 0);
        
        if (negativeAppointments.length === 0) {
            return appointmentsCache;
        }

        // Extract unique client IDs
        const uniqueClientIds = [...new Set(negativeAppointments.map(a => a.clientId))];

        // Skip if offline
        if (typeof navigator === 'undefined' || !navigator.onLine || api.isOfflineAtLogin()) {
            return appointmentsCache;
        }

        // Fetch appointments for each unique client
        const fetchPromises = uniqueClientIds.map(async (clientId) => {
            try {
                const fetchConfig = this.getFetchConfig(config, { "patientId": clientId });
                const response = await api.post(this.readUrl, fetchConfig);
                
                if (response?.resources) {
                    const mappedAppointments = response.resources.map(a => ({
                        appointmentId: a.activityLogId || a.appointmentId,
                        syncId: a.syncId,
                        clientId: clientId,
                        appointmentDate: a.appointmentDate,
                        startTime: String(a.beginTime || a.startTime).trim(),
                        endTime: String(a.endTime).trim(),
                        activityId: a.activityId,
                        staffId: a.staffId,
                        status: a.status,
                        evvVisitId: a.visitId || a.evvVisitId,
                        activityDetailId: a.activityDetailId
                    }));
                    
                    appointmentsCache.set(clientId, mappedAppointments);
                } else {
                    appointmentsCache.set(clientId, []);
                }
            } catch (err) {
                console.log(`[preloadAppointmentsByClients] Failed to fetch for clientId ${clientId}, will use IndexedDB fallback:`, err);
            }
        });

        await Promise.all(fetchPromises);

        return appointmentsCache;
    }

    findAppointmentAlreadySynced = async (dirtyAppointment, config, appointmentsCache = null) => {
        let allPositive = [];

        // Use preloaded cache if available (performance optimization)
        if (appointmentsCache && appointmentsCache.has(dirtyAppointment.clientId)) {
            allPositive = appointmentsCache.get(dirtyAppointment.clientId);
        } else {
            // Live Fetch from Oracle because Write happens before Read,
            // so IndexedDB might not have the Positive ID yet!
            if (typeof navigator !== 'undefined' && navigator.onLine && !api.isOfflineAtLogin()) {
                try {
                    const fetchConfig = this.getFetchConfig(config, { "patientId": dirtyAppointment.clientId });
                    const response = await api.post(this.readUrl, fetchConfig);
                    if (response?.resources) {
                        allPositive = response.resources.map(a => ({
                            appointmentId: a.activityLogId || a.appointmentId,
                            syncId: a.syncId,
                            clientId: dirtyAppointment.clientId,
                            appointmentDate: a.appointmentDate,
                            startTime: String(a.beginTime || a.startTime).trim(),
                            endTime: String(a.endTime).trim(),
                            activityId: a.activityId,
                            staffId: a.staffId,
                            status: a.status,
                            evvVisitId: a.visitId || a.evvVisitId,
                            activityDetailId: a.activityDetailId
                        }));
                    }
                } catch (err) {
                    console.log("Live deduplication fetch failed, falling back to IndexedDB.", err);
                }
            }

            // Fallback to IndexedDB if network fetch failed or returned nothing
            if (allPositive.length === 0) {
                allPositive = await evvRepository.evvDb.appointment
                    .filter(a => a.appointmentId > 0 && a.clientId === dirtyAppointment.clientId)
                    .toArray();
            }
        }

        let match;

        for (const a of allPositive) {
            // 1. Try exact syncId match (Safest)
            if (a.syncId && dirtyAppointment.syncId && a.syncId === dirtyAppointment.syncId) {
                match = a;
                break;
            }

            // 2. Fallback to heuristic match
            const syncIdMatch = String(a.syncId) === String(dirtyAppointment.syncId);

            if (syncIdMatch) {
                match = a;
                break;
            }
        }

        return match;
    }

    writeToServer = (config) => {
        if (config && config.skipWrite) {
            return Promise.resolve();
        }
        
        console.log('AppointmentService.writeToServer - config:');
        console.log(config);
        return this.repository.loadAllByProperty('syncState', 'dirty')
            .then(appointments => {
                if (appointments && appointments.length > 0){
                    return this.writeAppointmentsToServer(this.afterLoad(appointments, config), config)
                        .then(savedAppointments => syncService.visitService.dependentWriteToServer(config, savedAppointments));
                } else {
                    return syncService.visitService.dependentWriteToServer(config)
                        .then(() => Promise.resolve(appointments));
                }
            })
            .catch(error => {
                console.log("Error thrown while writing to Appointment server: ");
                console.log(error);
            })
    }

    isSyncRequired = () => {
        return this.repository.loadAllByProperty('syncState', 'dirty')
            .then(appointments => {
                if (appointments.length > 0){
                    return Promise.resolve(true);
                }

                return syncService.visitService.isSyncRequired();
            });
    }

    getAppointmentsForUnSignedDocs = () => {
        return evvRepository?.evvDb?.appointment
            .where("status").anyOf("Kept", "Co-Staff", "In Session", "Checked In")
            .toArray();
    }

    checkHasAppointment = (appointmentId) => {
        return this.repository.loadAllByProperty('appointmentId', appointmentId)
            .then(appointment => {
                if(appointment && appointment.length > 0) {
                    return appointment;
                }
            })
    }

    getAppointmentActivity = (activityId) => {
        return evvRepository?.evvDb?.activity.where({activityId: activityId}).toArray();
    }


    updateAppointmentDocumentDb = async (appointments) => {
        for (const appointment of appointments) {
            try {
                const existing = await evvRepository.evvDb.appointmentDocument.get(appointment.appointmentId);
                if (existing) {
                    await evvRepository.evvDb.appointmentDocument.update(appointment.appointmentId, {
                        title: appointment?.title ? appointment.title : null,
                        activityId: appointment?.activityId ? appointment.activityId : null,
                        programId: appointment?.programId ? appointment.programId : null,
                        appointmentDate: appointment?.appointmentDate ? appointment.appointmentDate : null,
                        appointmentOutRange: appointment?.appointmentOutRange ? appointment.appointmentOutRange : null,
                        appointmentType: appointment?.appointmentType ? appointment.appointmentType : null,
                        status: appointment?.status ? appointment.status : null,
                        start: appointment?.start ? appointment.start : null,
                        end: appointment?.end ? appointment.end : null,
                        startTime: appointment?.startTime ? appointment.startTime : null,
                        endTime: appointment?.endTime ? appointment.endTime : null,
                        duration: appointment?.duration ? appointment.duration : null,
                        staffId: appointment?.staffId ? appointment.staffId : null,
                        serviceLocationId: appointment?.serviceLocationId ? appointment.serviceLocationId : null,
                        actualStartTime: appointment?.actualStartTime ? appointment.actualStartTime : null,
                        actualEndTime: appointment?.actualEndTime ? appointment.actualEndTime : null,
                        organizationId: appointment?.organizationId ? appointment.organizationId : null,
                        nonBillableMinutes: appointment?.nonBillableMinutes ? appointment.nonBillableMinutes : null,
                        evvVisitId: appointment?.evvVisitId ? appointment.evvVisitId : null,
                    });
                }
            } catch (e) {
                console.error('Error saving/updating in appointmentDocument:', e);
            }
        }
    }

}

export default AppointmentService;
